import { lazy, type ComponentType } from 'react';

/**
 * How long after an automatic reload a second chunk failure for the SAME module is treated as
 * "the reload did not help" (re-thrown to the error boundary) rather than as a fresh, unrelated
 * stale-chunk event that deserves its own reload.
 *
 * A stale chunk is created by a deployment (the new image contains only the new hashed assets),
 * and a tab that stays open for days sees one such event PER DEPLOY. A once-per-session flag
 * therefore only protects the first deploy of a tab's life; the second sends the user straight
 * to the error card. A time window keeps the infinite-loop guard while letting every deploy
 * self-heal.
 */
export const CHUNK_RETRY_WINDOW_MS = 30_000;

/**
 * Wraps React.lazy() to automatically reload the page once when a dynamic
 * import fails due to stale chunk hashes (e.g. after a new deployment).
 *
 * Uses sessionStorage (a per-module timestamp of the last auto-reload) to prevent reload
 * loops: if a reload for this module happened within {@link CHUNK_RETRY_WINDOW_MS}, the error
 * is re-thrown so the error boundary can display the manual retry UI. A successful import
 * clears the marker, so the next deployment gets a fresh automatic reload.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
  moduleId?: string,
) {
  const key = `chunk-retry:${moduleId ?? factory.toString()}`;

  // Two-argument then(): keeps success and failure handling to a single microtask hop and makes
  // sure the failure branch only ever sees the import's own rejection.
  return lazy(() =>
    factory().then(
      (module) => {
        // The chunk loaded — a previous auto-reload (if any) did its job. Clear the marker so
        // a later deployment in this same tab session can self-heal again.
        safeRemove(key);
        return module;
      },
      (error: unknown) => {
        // Only auto-reload for chunk / dynamic import failures
        if (!isChunkLoadError(error)) throw error;

        const lastReloadAt = readTimestamp(key);
        const reloadedRecently =
          lastReloadAt !== null && Date.now() - lastReloadAt < CHUNK_RETRY_WINDOW_MS;

        if (!reloadedRecently) {
          safeSet(key, String(Date.now()));
          window.location.reload();
          // Return a never-resolving promise so React doesn't try to render
          // while the page is reloading.
          return new Promise<{ default: T }>(() => {});
        }

        // A reload within the window did not fix it — clear the marker and let the error
        // boundary show the manual retry UI.
        safeRemove(key);
        throw error;
      },
    ),
  );
}

function readTimestamp(key: string): number | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (raw === null) return null;
    const parsed = Number(raw);
    // Legacy '1' marker (pre-timestamp builds) parses as 1 — effectively "long ago", so a
    // tab carrying the old flag still gets one reload instead of being stuck on the error card.
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Storage unavailable (private mode / quota) — still reload; the loop guard is best-effort.
  }
}

function safeRemove(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (error.name === 'ChunkLoadError') return true;

  const msg = error.message.toLowerCase();
  return (
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk') ||
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('importing a module script failed')
  );
}
